import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import Layout from '../../components/Layout';
import './Billing.css';

const PLANS = {
  starter: {
    name: 'Starter', price: 250, color: '#9ca3af',
    limits: { users: 20, warehouses: 1, locations: 15 },
    features: ['Live job queue', 'Kiosk mode', 'QR job labels', 'Inventory sync', 'Email support'],
  },
  pro: {
    name: 'Pro', price: 650, color: '#4f8ef7',
    limits: { users: 250, warehouses: 3, locations: 150 },
    features: ['Everything in Starter', 'Floor audit', 'Full audit log', 'Priority support'],
  },
  premium: {
    name: 'Premium', price: 1200, color: '#22c55e',
    limits: { users: Infinity, warehouses: Infinity, locations: Infinity },
    features: ['Everything in Pro', 'Dedicated onboarding', 'Phone support', 'SLA guarantee', 'Account manager'],
  },
};

const PLAN_ORDER = ['starter', 'pro', 'premium'];

function Confetti({ active }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const pieces = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: -10 - Math.random() * 40,
      r: 4 + Math.random() * 5,
      d: 1 + Math.random() * 2,
      color: ['#4f8ef7','#22c55e','#f59e0b','#f0f0f2','#8b5cf6'][Math.floor(Math.random()*5)],
      tilt: Math.random() * 10 - 5,
      tiltV: 0.1 + Math.random() * 0.2,
    }));
    let frame;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => {
        ctx.save();
ctx.translate(p.x, p.y);
ctx.rotate(p.tilt * 0.1);
ctx.fillStyle = p.color;
ctx.fillRect(-p.r, -p.r * 0.5, p.r * 2, p.r);
ctx.restore();
        p.y += p.d; p.x += p.tilt * 0.3; p.tilt += p.tiltV;
      });
      if (pieces.some(p => p.y < canvas.height)) frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [active]);
  if (!active) return null;
  return <canvas ref={canvasRef} className="bill-confetti" />;
}

export default function Billing() {
  const { org, updateOrg }          = useAuth();
  const [usage, setUsage]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgraded, setUpgraded]     = useState(null);
  const [confetti, setConfetti]     = useState(false);

  const planKey     = org?.plan || 'starter';
  const currentPlan = PLANS[planKey] || PLANS.starter;
  const higherPlans = PLAN_ORDER.filter(p => PLAN_ORDER.indexOf(p) > PLAN_ORDER.indexOf(planKey));

  const renewDate = new Date();
  renewDate.setMonth(renewDate.getMonth() + 1);
  const renewStr = renewDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  useEffect(() => { loadUsage(); }, []);

  const loadUsage = async () => {
    try {
      const [usersRes, locsRes] = await Promise.all([
        client.get('/org/users'),
        client.get('/locations'),
      ]);
      setUsage({ users: usersRes.data.length, locations: locsRes.data.length, warehouses: 1 });
    } catch { } finally { setLoading(false); }
  };

  const handleUpgrade = async (newPlan) => {
    // Stripe not wired yet -- just update plan optimistically
    try {
      await client.put('/org', { plan: newPlan });
      updateOrg({ plan: newPlan });
      setUpgraded(newPlan);
      setShowUpgrade(false);
      setConfetti(true);
      setTimeout(() => setConfetti(false), 3500);
    } catch (err) {
      alert('Failed to upgrade. Please try again.');
    }
  };

  const usagePct = (used, limit) => limit === Infinity ? 0 : Math.min((used / limit) * 100, 100);
  const usageColor = (pct) => pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#4f8ef7';

  if (loading) return <Layout><div style={{color:'#6b7280',padding:'20px'}}>Loading...</div></Layout>;

  const unlocked = upgraded ? PLANS[upgraded]?.features : null;

  return (
    <Layout>
      <Confetti active={confetti} />

      <div className="spage-header">
        <h1>Billing</h1>
        <p className="spage-sub">Manage your plan, payment method, and invoices.</p>
      </div>

      {/* Upgrade success */}
      {upgraded && (
        <div className="bill-unlock-card">
          <div className="bill-unlock-title">You upgraded to {PLANS[upgraded]?.name}!</div>
          <div className="bill-unlock-sub">Here's what you unlocked:</div>
          <div className="bill-unlock-list">
            {unlocked?.map(f => <div key={f} className="bill-unlock-item"><span className="bill-chk">✓</span>{f}</div>)}
          </div>
        </div>
      )}

      {/* Current Plan */}
      <div className="spage-section">
        <div className="spage-section-label">CURRENT PLAN</div>
        <div className="bill-plan-row">
          <div>
            <div className="bill-plan-name" style={{color: currentPlan.color}}>{planKey.toUpperCase()}</div>
            <div className="bill-plan-price"><span className="bill-price-big">${currentPlan.price}</span><span className="bill-price-mo">/mo</span></div>
            <div className="bill-renew">Renews {renewStr}</div>
          </div>
          <div className="bill-plan-actions">
            {higherPlans.length > 0 && (
              <div className="bill-upgrade-wrap">
                <button className="bill-btn-primary" onClick={() => setShowUpgrade(!showUpgrade)}>
                  Upgrade Plan ▾
                </button>
                {showUpgrade && (
                  <div className="bill-upgrade-dropdown">
                    {higherPlans.map(p => (
                      <div key={p} className="bill-dropdown-item" onClick={() => handleUpgrade(p)}>
                        <div className="bill-dd-name" style={{color: PLANS[p].color}}>{PLANS[p].name}</div>
                        <div className="bill-dd-price">${PLANS[p].price}/mo</div>
                        <div className="bill-dd-limits">
                          {PLANS[p].limits.users === Infinity ? '∞' : PLANS[p].limits.users} users ·{' '}
                          {PLANS[p].limits.warehouses === Infinity ? '∞' : PLANS[p].limits.warehouses} warehouses ·{' '}
                          {PLANS[p].limits.locations === Infinity ? '∞' : PLANS[p].limits.locations} locations
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {planKey !== 'starter' && (
              <button className="bill-btn-secondary">Change Plan</button>
            )}
          </div>
        </div>
      </div>

      {/* Usage */}
      <div className="spage-section">
        <div className="spage-section-label">USAGE</div>
        {[
          { key: 'users',      label: 'Users' },
          { key: 'warehouses', label: 'Warehouses' },
          { key: 'locations',  label: 'Locations' },
        ].map(({ key, label }) => {
          const used  = usage?.[key] || 0;
          const limit = currentPlan.limits[key];
          const pct   = usagePct(used, limit);
          const col   = usageColor(pct);
          return (
            <div key={key} className="bill-usage-row">
              <div className="bill-usage-label">{label}</div>
              <div className="bill-usage-bar-wrap">
                <div className="bill-usage-bar">
                  {limit !== Infinity && <div className="bill-usage-fill" style={{width:`${pct}%`,background:col}} />}
                </div>
              </div>
              <div className="bill-usage-num" style={{color: limit === Infinity ? '#22c55e' : col}}>
                {used} / {limit === Infinity ? '∞' : limit}
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment Method */}
      <div className="spage-section">
        <div className="spage-section-label">PAYMENT METHOD</div>
        <div className="bill-payment-row">
          <div className="bill-card-display">
            <div>
              <div className="bill-card-num">•••• •••• •••• ––––</div>
              <div className="bill-card-exp">Add a card to enable billing</div>
            </div>
          </div>
          <button className="bill-btn-secondary" onClick={() => alert('Stripe billing coming soon')}>Update</button>
        </div>
      </div>

      {/* Billing History */}
      <div className="spage-section">
        <div className="spage-section-label">BILLING HISTORY</div>
        <table className="bill-table">
          <thead>
            <tr>
              <th>INVOICE</th><th>DATE</th><th>AMOUNT</th><th>STATUS</th><th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            <tr><td colSpan="5" className="bill-table-empty">No invoices yet.</td></tr>
          </tbody>
        </table>
      </div>
    </Layout>
  );
}